import {
    inject,
    Injectable,
    Injector,
    NgZone,
    signal,
} from '@angular/core';

import {
    ConfigurationManagerService,
} from 'src/app/core/services/configuration-manager/configuration-manager.service';

export type AppLoaderMode = 'indeterminate' | 'determinate' | 'buffer';

/** What the application loader can be told. A field that is not given keeps its value (the mode is the exception, see `update`). */
export interface AppLoaderStatus {
    enabled?: boolean;
    mode?: AppLoaderMode;
    value?: number;
    mainMessage?: string;
    otherMessage?: string;
    showLogo?: boolean;
    updateMode?: boolean;
}

/**
 * State of the full screen loader of the application (the splash with the logo and the progress): shown while a module
 * loads, while the shop data is downloaded and while the app updates. `AppLoaderComponent` draws it. It is the Angular
 * version of the `backLoader` instance of the AngularJS `loader` directive, with the same rules.
 */
@Injectable({
    providedIn: 'root',
})
export class AppLoaderService {
    private readonly injector = inject(Injector);
    private readonly zone = inject(NgZone);

    public readonly enabled = signal(false);
    public readonly mode = signal<AppLoaderMode>('indeterminate');
    public readonly value = signal(0);
    public readonly mainMessage = signal('');
    public readonly otherMessage = signal('');
    public readonly showLogo = signal(false);
    public readonly updateMode = signal(false);
    public readonly logoUrl = signal('');

    public show(): void {
        this.update({ enabled: true });
    }

    public hide(): void {
        this.update({ enabled: false });
    }

    /**
     * Same rules as the old directive: the mode goes back to `indeterminate` at every call that does not give one, the
     * value only changes when it is a non-zero number (and only matters in `determinate` and `buffer`), the logo is chosen
     * again while it is shown. Runs in the Angular zone: the callers of the Electron updater are outside it, and
     * Angular 17 does not look at signals changed there.
     */
    public update(status: AppLoaderStatus): void {
        this.zone.run(() => {
            this.mode.set(status.mode || 'indeterminate');

            if (this.mode() === 'buffer' || this.mode() === 'determinate') {
                const value = Math.trunc(Number(status.value));

                if (Number.isFinite(value) && value !== 0) {
                    this.value.set(value);
                }
            }

            if (status.mainMessage !== undefined) {
                this.mainMessage.set(status.mainMessage);
            }

            if (status.otherMessage !== undefined) {
                this.otherMessage.set(status.otherMessage);
            }

            if (status.enabled !== undefined) {
                this.enabled.set(status.enabled);
            }

            if (status.showLogo !== undefined) {
                this.showLogo.set(status.showLogo);
            }

            if (status.updateMode !== undefined) {
                this.updateMode.set(status.updateMode);
            }

            if (this.showLogo()) {
                this.chooseLogo();
            }
        });
    }

    /**
     * The configuration manager is taken only now, not when the service is created: it builds the entity manager and the
     * storage, which must not start before AngularJS is bootstrapped (the loader is created with the first Angular view).
     * The logo is only asked for once the shop data is being loaded, long after that.
     */
    private chooseLogo(): void {
        try {
            const configuration = this.injector.get(ConfigurationManagerService);

            if (configuration.getSetting('startup.show_pc_cash_logo')) {
                this.logoUrl.set('pc_cash_air.png');
            } else {
                this.logoUrl.set((this.updateMode() || configuration.getShopCountry()) !== 'IT' ? 'logo.png' : 'morph.gif');
            }
        } catch {
            // The configuration is not ready: keep the logo the loader already has
        }
    }
}
